import { useEffect, useReducer, useState } from "react";
import { Table, Tag, Input, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
    ClientMemberColumns,
    IUserRecord,
    ParentModule,
} from "@/ts/common-types";
import { getClientUsers, updateClientUser } from "@/ts/retrieve-data";
import { showToast } from "@/ts/modals";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import EditUserModal from "@components/modals/EditUserModal";
import { GrEdit } from "react-icons/gr";
import { Row, Col } from "antd";
import { usePermissionCheck, useUserTimezone } from "@/hooks/AllCustomHooks";
import { DEFAULT_PAGE_SIZE_OPTIONS, DateTimeFormats } from "@/ts/constants";
import { convertIsoDate } from "@/ts/utils";
import { ExpirationDateCell } from "../components/ExpirationDateCell";
import { useNavigate } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

interface ClientMembersProps {
    clientId: number;
    clientTimezone?: string;
}

type DataIndex = (typeof ClientMemberColumns)[keyof typeof ClientMemberColumns];

interface ILoadingState {
    loading: boolean;
    editLoading: boolean;
}

type TLoadingAction =
    | { type: "SET_LOADING"; payload: boolean }
    | { type: "SET_EDIT_LOADING"; payload: boolean };

const loadingReducer = (
    state: ILoadingState,
    action: TLoadingAction
): ILoadingState => {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload };
        case "SET_EDIT_LOADING":
            return { ...state, editLoading: action.payload };
        default:
            return state;
    }
};

interface IUsersState {
    data: IUserRecord[];
    total: number;
}

const getFullName = (user: IUserRecord): string => {
    return `${user.first_name} ${user.last_name || ""}`.trim();
};

const ClientMembers: React.FC<ClientMembersProps> = ({
    clientId,
    clientTimezone,
}) => {
    const navigate = useNavigate();
    const [usersState, setUsersState] = useState<IUsersState>({
        data: [],
        total: 0,
    });
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [editingUser, setEditingUser] = useState<IUserRecord | null>(null);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const { canUpdateUser } = usePermissionCheck();
    const currentTimeZone = useUserTimezone(clientTimezone);

    const initialLoadingState: ILoadingState = {
        loading: true,
        editLoading: false,
    };
    const [loadingState, dispatch] = useReducer(
        loadingReducer,
        initialLoadingState
    );

    const fetchUsers = async (
        page: number = currentPage,
        size: number = pageSize
    ) => {
        try {
            dispatch({ type: "SET_LOADING", payload: true });
            const data = await getClientUsers(clientId, page, size);
            setUsersState({
                data: data.users,
                total: data.total,
            });
        } catch (error: any) {
            showToast("danger", error.message || "Failed to load users");
        } finally {
            dispatch({ type: "SET_LOADING", payload: false });
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [clientId, currentPage, pageSize]);

    const getRoleFilters = () => {
        const roles = [
            ...new Set(
                usersState.data.map((user) => user.access[0]?.role_name)
            ),
        ];
        return roles
            .filter(Boolean)
            .map((role) => ({ text: role, value: role }));
    };

    const getColumnSearchProps = (dataIndex: DataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }: any) => (
            <div className="p-2">
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() => confirm()}
                    style={{ width: 188, marginBottom: 8, display: "block" }}
                />
                <div className="d-flex justify-content-between">
                    <button
                        type="button"
                        onClick={() => clearFilters && clearFilters()}
                        className="btn btn-sm btn-light"
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        onClick={() => confirm()}
                        className="btn btn-sm btn-primary"
                    >
                        Filter
                    </button>
                </div>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined
                style={{ color: filtered ? "#1890ff" : undefined }}
            />
        ),
        onFilter: (value: string | number | boolean, record: IUserRecord) => {
            if (typeof value !== "string") return false;

            if (dataIndex === ClientMemberColumns.FullName) {
                return getFullName(record)
                    .toLowerCase()
                    .includes(value.toLowerCase());
            }

            if (dataIndex === ClientMemberColumns.Email) {
                return record.email.toLowerCase().includes(value.toLowerCase());
            }
            if (dataIndex === ClientMemberColumns.Country && record.country) {
                return record.country
                    .toLowerCase()
                    .includes(value.toLowerCase());
            }
            if (dataIndex === ClientMemberColumns.Timezone && record.timezone) {
                return record.timezone
                    .toLowerCase()
                    .includes(value.toLowerCase());
            }
            if (
                dataIndex === ClientMemberColumns.LastLogin &&
                record.last_login
            ) {
                return record.last_login
                    .toLowerCase()
                    .includes(value.toLowerCase());
            }

            return false;
        },
    });

    const handleEdit = (user: IUserRecord) => {
        setEditingUser(user);
        setEditModalVisible(true);
    };

    const handleCancel = () => {
        setEditingUser(null);
        setEditModalVisible(false);
    };

    const handleSave = async (values: Partial<IUserRecord>) => {
        if (!editingUser) return;

        try {
            dispatch({ type: "SET_EDIT_LOADING", payload: true });
            await updateClientUser(clientId, editingUser.user_id, values);
            showToast("success", "User updated successfully");

            // Refresh the users list
            const data = await getClientUsers(clientId);
            setUsersState({
                data: data.users,
                total: data.total,
            });

            setEditModalVisible(false);
            setEditingUser(null);
        } catch (error: any) {
            showToast("danger", error.message || "Failed to update user");
        } finally {
            dispatch({ type: "SET_EDIT_LOADING", payload: false });
        }
    };

    const columns: ColumnsType<IUserRecord> = [
        {
            title: "Name",
            key: ClientMemberColumns.Name,
            render: (_: unknown, record: IUserRecord) => (
                <span
                    className="text-primary cursor-pointer"
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                            `/${ParentModule.ADMIN_SETTINGS}/settings/users/${record.user_id}`
                        );
                    }}
                >
                    {getFullName(record)}
                </span>
            ),
            sorter: (a: IUserRecord, b: IUserRecord) => {
                return getFullName(a).localeCompare(getFullName(b));
            },
            ...getColumnSearchProps(ClientMemberColumns.FullName),
        },
        {
            title: "Email",
            dataIndex: ClientMemberColumns.Email,
            key: ClientMemberColumns.Email,
            sorter: (a: IUserRecord, b: IUserRecord) =>
                a.email.localeCompare(b.email),
            ...getColumnSearchProps(ClientMemberColumns.Email),
        },
        {
            title: "Role",
            key: ClientMemberColumns.Role,
            render: (_: unknown, record: IUserRecord) => (
                <Tag color="blue">
                    {record.access[0]?.role_name || record.access[0]?.role_slug}
                </Tag>
            ),
            filters: getRoleFilters(),
            onFilter: (value: string | number | boolean, record: IUserRecord) =>
                record.access[0]?.role_name === value,
        },
        {
            title: "Status",
            key: ClientMemberColumns.Status,
            render: (_: unknown, record: IUserRecord) => (
                <Tag color={record.status ? "green" : "red"}>
                    {record.status ? "Active" : "Inactive"}
                </Tag>
            ),
            filters: [
                { text: "Active", value: true },
                { text: "Inactive", value: false },
            ],
            onFilter: (value: string | number | boolean, record: IUserRecord) =>
                record.status === value,
        },
        {
            title: "Country",
            dataIndex: ClientMemberColumns.Country,
            key: ClientMemberColumns.Country,
            sorter: (a: IUserRecord, b: IUserRecord) => {
                if (!a.country || !b.country) return 0;
                return a.country.localeCompare(b.country);
            },
            ...getColumnSearchProps(ClientMemberColumns.Country),
        },
        {
            title: "Timezone",
            dataIndex: ClientMemberColumns.Timezone,
            key: ClientMemberColumns.Timezone,
            sorter: (a: IUserRecord, b: IUserRecord) => {
                if (!a.timezone || !b.timezone) return 0;
                return a.timezone.localeCompare(b.timezone);
            },
            ...getColumnSearchProps(ClientMemberColumns.Timezone),
        },
        {
            title: "Last Login",
            key: ClientMemberColumns.LastLogin,
            render: (_: unknown, record: IUserRecord) => (
                <div>
                    <div>
                        {!record.last_login
                            ? "Never logged in"
                            : convertIsoDate(
                                  String(record.last_login),
                                  currentTimeZone
                              )}
                    </div>
                    <div className="small text-muted">
                        {record.last_login
                            ? `${convertIsoDate(String(record.last_login))} ${
                                  DateTimeFormats.UTC_SUFFIX
                              }`
                            : ""}
                    </div>
                </div>
            ),
        },
        {
            title: "Expiration",
            key: ClientMemberColumns.ExpiryDate,
            render: (_: unknown, record: IUserRecord) => (
                <ExpirationDateCell
                    record={record}
                    currentTimeZone={currentTimeZone}
                />
            ),
        },
        canUpdateUser && {
            title: "Action",
            key: ClientMemberColumns.Action,
            render: (_: unknown, record: IUserRecord) => (
                <GrEdit
                    className="fs-5 cursor-pointer"
                    onClick={() => handleEdit(record)}
                />
            ),
        },
    ].filter(Boolean) as ColumnsType<IUserRecord>;

    if (loadingState.loading) {
        return (
            <div className="text-center">
                <Spin />
            </div>
        );
    }

    return (
        <div className="client-members-container">
            <Row justify="space-between" align="middle" className="mb-4">
                <Col>
                    <h5>
                        All Members{" "}
                        <span className="text-muted">({usersState.total})</span>
                    </h5>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={usersState.data}
                rowKey="user_id"
                loading={loadingState.loading}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: usersState.total,
                    showSizeChanger: true,
                    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                    defaultPageSize: 10,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                }}
                scroll={{ y: 390 }}
            />

            {canUpdateUser && (
                <EditUserModal
                    user={editingUser}
                    visible={editModalVisible}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    roles={usersState.data.reduce((acc, user) => {
                        const access = user.access[0];
                        if (
                            access &&
                            !acc.some((r) => r.id === access.role_id)
                        ) {
                            acc.push({
                                id: access.role_id,
                                name: access.role_slug,
                            });
                        }
                        return acc;
                    }, [] as { id: number; name: string }[])}
                    loading={loadingState.editLoading}
                />
            )}
        </div>
    );
};

export default ClientMembers;
